import React from "react";
import { Category } from "@/types/general";
import { useGetModuleCategoriesQuery } from "@redux/features/categories/categoriesApi";
import BaseCommandDropdown, {
    BaseCommandDropdownProps,
    SelectionEvent,
    TriggerProps,
    useCommandDropdown,
} from "@reusables/BaseCommandDropdown";
import { useTranslation } from "react-i18next";
import BaseBadge from "@reusables/BaseBadge";

export type CategoryCommandDropdownProps = {
    value?: BaseCommandDropdownProps["value"];
    onSelect?: (event: SelectionEvent) => void;
    multiple?: boolean;
    module?: string;
    error?: TriggerProps["error"];
    options?: Category.Root[];
};

export default function CategoryCommandDropdown({
    value,
    onSelect,
    multiple = false,
    module = "product",
    error,
    options,
}: CategoryCommandDropdownProps) {
    const { t } = useTranslation("", { keyPrefix: "general.components.categoryCommandDropdown" });

    const { data: fetchedOptions, isLoading: isQueryLoading } = useGetModuleCategoriesQuery(
        module,
        { skip: !!options }
    );

    const resolvedOptions = options ?? fetchedOptions;
    const isLoading = options ? false : isQueryLoading;

    const modalHeading = multiple ? t("heading.multiple") : t("heading.single");
    const serviceBadgeLabel = t("serviceBadge");

    const getKey = React.useCallback((cat: Category.Root) => cat.id, []);
    const getLabel = React.useCallback((cat: Category.Root) => cat.name, []);
    const getChildren = React.useCallback((cat: Category.Root) => cat.children ?? [], []);

    const findPathById = React.useCallback(
        (
            items: Category.Root[] | undefined,
            id: number | string,
            parentPath: Array<number | string> = []
        ): Array<number | string> | null => {
            if (!items?.length) return null;
            for (const item of items) {
                const nextPath = [...parentPath, item.id];
                if (item.id === id) {
                    return nextPath;
                }
                const childPath = findPathById(item.children, id, nextPath);
                if (childPath) {
                    return childPath;
                }
            }
            return null;
        },
        []
    );

    const resolvedValue = React.useMemo(() => {
        if (value === null || value === undefined) return value;

        if (Array.isArray(value)) {
            if (value.length === 0) return value;
            if (Array.isArray(value[0])) return value;
            return (value as Array<number | string>).map(
                (id) => findPathById(resolvedOptions, id) ?? id
            );
        }

        if (typeof value === "number" || typeof value === "string") {
            return findPathById(resolvedOptions, value) ?? value;
        }

        return value;
    }, [findPathById, resolvedOptions, value]);

    return (
        <BaseCommandDropdown
            filterMode="all-levels"
            onSelect={onSelect}
            value={resolvedValue as any}
            multiple={multiple}
        >
            <RegistryHydrator
                options={resolvedOptions}
                getKey={getKey}
                getLabel={getLabel}
                getChildren={getChildren}
            />
            <BaseCommandDropdown.Trigger
                getLabel={(path, labels) => labels.join(" > ")}
                isLoading={isLoading}
                error={error}
                placeholder={modalHeading}
            />

            <BaseCommandDropdown.Modal title={modalHeading}>
                <BaseCommandDropdown.Search />

                <BaseCommandDropdown.Content maxHeight={600}>
                    <BaseCommandDropdown.Empty>{t("noResults")}</BaseCommandDropdown.Empty>
                    <CategoryLevel
                        options={resolvedOptions}
                        serviceBadgeLabel={serviceBadgeLabel}
                        getKey={getKey}
                        getLabel={getLabel}
                    />
                </BaseCommandDropdown.Content>
            </BaseCommandDropdown.Modal>
        </BaseCommandDropdown>
    );
}

function CategoryLevel({
    options,
    serviceBadgeLabel,
    getKey,
    getLabel,
}: {
    options?: Category.Root[];
    serviceBadgeLabel: string;
    getKey: (cat: Category.Root) => number;
    getLabel: (cat: Category.Root) => string;
}) {
    return (
        <BaseCommandDropdown.Level
            options={options}
            getKey={getKey}
            getLabel={getLabel}
            getCode={(cat) => cat.code}
            hasChildren={(cat) => (cat.children?.length ?? 0) > 0}
            renderItem={(cat, { checkElement, chevronElement }) => (
                <div className="flex flex-row items-center justify-between w-full">
                    <div className="flex items-center gap-2 text-accent">
                        <span>{cat.name}</span>
                        {cat.is_service && <BaseBadge>{serviceBadgeLabel}</BaseBadge>}
                    </div>
                    <div className="flex items-center gap-2">
                        {cat.code && (
                            <span className="text-lightGreen-500 font-thin">{cat.code}</span>
                        )}
                        {checkElement}
                        {chevronElement}
                    </div>
                </div>
            )}
            allowParentSelect
        >
            {(parentCat) => (
                <CategoryLevel
                    options={parentCat.children}
                    serviceBadgeLabel={serviceBadgeLabel}
                    getKey={getKey}
                    getLabel={getLabel}
                />
            )}
        </BaseCommandDropdown.Level>
    );
}

function RegistryHydrator({
    options,
    getKey,
    getLabel,
    getChildren,
}: {
    options?: Category.Root[];
    getKey: (cat: Category.Root) => number;
    getLabel: (cat: Category.Root) => string;
    getChildren: (cat: Category.Root) => Category.Root[];
}) {
    const { registerLabel, registerPathForId, registerItemForPath } = useCommandDropdown();

    React.useEffect(() => {
        if (!options?.length) return;

        const traverse = (items: Category.Root[], parentPath: Array<number | string>) => {
            items.forEach((item) => {
                const key = getKey(item);
                const path = [...parentPath, key];
                registerLabel(path, getLabel(item));
                registerPathForId(key, path);
                registerItemForPath(path, item);
                const children = getChildren(item);
                if (children.length) {
                    traverse(children, path);
                }
            });
        };

        traverse(options, []);
    }, [options, getKey, getLabel, getChildren, registerLabel, registerPathForId, registerItemForPath]);

    return null;
}
